import { Box, Autocomplete, TextField, Chip, Typography, SxProps, useTheme, Checkbox } from '@mui/material';

type CustomAutocompleteProps = {
  label?: string;
  value?: any[];
  onChange: (value: any[]) => void;
  options: { value: any, label: string }[];
  sx?: SxProps;
  backgroundColor?: string;
  borderEnabled?: boolean;
  required?: boolean;
  placeholder?: string;
  error?: boolean;
  helperText?: string;
  multiple?: boolean;
};

export default function CustomAutocomplete({ 
  placeholder, 
  borderEnabled, 
  backgroundColor, 
  label, 
  value = [], 
  onChange, 
  options, 
  sx, 
  required, 
  error, 
  helperText,
  multiple = true 
}: CustomAutocompleteProps) {
  const theme = useTheme();

  const handleChange = (event: any, newValue: any) => {
    if (multiple) {
      onChange(newValue || []);
    } else {
      onChange(newValue ? [newValue] : []);
    }
  };

  return (
    <Box>
      <Typography
        sx={{
          fontSize: 16,
          fontWeight: 500,
          color: error ? 'error.main' : '#9AA6A7',
          mb: 1
        }}
      >
        {label}
      </Typography>
      <Autocomplete
        multiple={multiple}
        options={options}
        getOptionLabel={(option) => option.label}
        value={value}
        onChange={handleChange}
        disableCloseOnSelect
        renderOption={(props, option, { selected }) => (
          <li {...props}>
            <Checkbox
              style={{ marginRight: 8 }}
              checked={selected}
              sx={{
                color: '#9AA6A7',
                '&.Mui-checked': {
                  color: '#344054',
                },
              }}
            />
            {option.label}
          </li>
        )}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => (
            <Chip
              variant="outlined"
              label={option.label}
              {...getTagProps({ index })}
              key={option.value}
              sx={{
                borderRadius: '20px',
                backgroundColor: '#F2F4F7',
                borderColor: '#9AA6A7',
                color: '#344054',
                '& .MuiChip-deleteIcon': {
                  color: '#9AA6A7',
                  '&:hover': {
                    color: '#344054'
                  }
                }
              }}
            />
          ))
        }
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={placeholder || 'Seçim yapınız'}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '60px',
                backgroundColor: backgroundColor || '#F2F4F7',
                '& fieldset': {
                  borderColor: error ? 'error.main' : '#9AA6A7',
                  borderWidth: borderEnabled ? 1 : 0,
                },
                '&:hover fieldset': {
                  borderWidth: borderEnabled ? 1 : 0,
                  borderColor: error ? 'error.main' : '#9AA6A7',
                },
                '&.Mui-focused fieldset': {
                  borderWidth: borderEnabled ? 1 : 0,
                  borderColor: error ? 'error.main' : '#9AA6A7',
                },
                '&.Mui-focused': {
                  borderRadius: '60px',
                },
              },
              ...sx
            }}
            error={error}
            required={required}
          />
        )}
        sx={{
          '& .MuiAutocomplete-tag': {
            borderRadius: '20px',
          },
          ...sx
        }}
      />
      {helperText && (
        <Typography
          variant="caption"
          sx={{
            color: error ? 'error.main' : 'inherit',
            ml: 2,
            mt: 0.5
          }}
        >
          {helperText}
        </Typography>
      )}
    </Box>
  );
}
